import { useState, useEffect, useCallback } from 'react';

const THEME_KEY = 'trade-volume-theme';

/**
 * 主题切换 Hook
 * 支持浅色（light）和深色（dark）主题
 */
export const useTheme = () => {
  // 初始化主题：优先从 localStorage 读取，其次检测系统偏好，默认深色
  const [theme, setTheme] = useState(() => {
    // 检查 localStorage
    const savedTheme = localStorage.getItem(THEME_KEY);
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }

    // 检测系统偏好
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }

    return 'dark';
  });

  // 应用主题到 DOM
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // 添加过渡类以启用平滑动画
    body.classList.add('theme-transitioning');

    // 使用 data-theme 属性
    root.setAttribute('data-theme', theme);

    // 同时添加 class 以便兼容
    root.classList.remove('theme-light', 'theme-dark');
    root.classList.add(`theme-${theme}`);

    // 保存到 localStorage
    localStorage.setItem(THEME_KEY, theme);

    // 更新 body 背景色以避免闪烁
    body.style.backgroundColor = theme === 'light' ? '#f8fafc' : '#0a0a0f';

    // 更新 meta theme-color（移动端浏览器地址栏颜色）
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === 'light' ? '#f8fafc' : '#0a0a0f');
    }

    // 移除过渡类（在动画完成后）
    const transitionTimeout = setTimeout(() => {
      body.classList.remove('theme-transitioning');
    }, 400); // 与 CSS 中的 --transition-theme 时间一致

    return () => clearTimeout(transitionTimeout);
  }, [theme]);

  // 监听系统主题变化
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // 只有当用户没有手动设置主题时才跟随系统
      const savedTheme = localStorage.getItem(THEME_KEY);
      if (!savedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // 同步不同组件间的实例状态
  useEffect(() => {
    const handleSync = (e) => {
      if (e.detail && e.detail !== theme) {
        setTheme(e.detail);
      }
    };
    window.addEventListener('theme-change-sync', handleSync);
    return () => window.removeEventListener('theme-change-sync', handleSync);
  }, [theme]);

  // 切换主题
  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const newTheme = prev === 'dark' ? 'light' : 'dark';
      window.dispatchEvent(new CustomEvent('theme-change-sync', { detail: newTheme }));
      return newTheme;
    });
  }, []);

  // 设置特定主题
  const setLightTheme = useCallback(() => {
    setTheme('light');
    window.dispatchEvent(new CustomEvent('theme-change-sync', { detail: 'light' }));
  }, []);
  const setDarkTheme = useCallback(() => {
    setTheme('dark');
    window.dispatchEvent(new CustomEvent('theme-change-sync', { detail: 'dark' }));
  }, []);

  // 检查当前主题
  const isDark = theme === 'dark';
  const isLight = theme === 'light';

  return {
    theme,
    toggleTheme,
    setLightTheme,
    setDarkTheme,
    isDark,
    isLight,
  };
};

export default useTheme;
